import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import useApi from 'api/api';
import SessionExpiredDialog from './SessionExpiredDialog';

interface AuthErrorContextType {
  // This can be extended in the future if needed
}

const AuthErrorContext = createContext<AuthErrorContextType | null>(null);

interface AuthErrorProviderProps {
  children: ReactNode;
}

// Error boundary component
class AuthErrorBoundary extends React.Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('🔴 AuthErrorProvider boundary caught error:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🔴 AuthErrorProvider error details:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

// Safe wrapper component
const AuthErrorProviderInner: React.FC<AuthErrorProviderProps> = ({ children }) => {
  
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Try to get useApi with error handling
  let sessionDialog;
  try {
    const apiHook = useApi();
    sessionDialog = apiHook.sessionDialog;
    
    if (!isReady) {
      setIsReady(true);
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    console.error('🔴 AuthErrorProvider: useApi failed:', errorMessage);
    
    if (error !== errorMessage) {
      setError(errorMessage);
    }
    
    // Provide fallback session dialog state
    sessionDialog = {
      isOpen: false,
      error: undefined,
      onConfirm: () => {},
      isInteractiveLoginAvailable: false
    };
  }

  useEffect(() => {
    // Component mounted - no logging needed
    return () => {
      // Component unmounting - no logging needed
    };
  }, []);

  // Render with error fallback if needed

  return (
    <AuthErrorContext.Provider value={{}}>
      {children}
      {isReady && (
        <SessionExpiredDialog
          open={sessionDialog.isOpen}
          onConfirm={sessionDialog.onConfirm}
          error={sessionDialog.error}
          isInteractiveLoginAvailable={sessionDialog.isInteractiveLoginAvailable}
        />
      )}
    </AuthErrorContext.Provider>
  );
};

export const AuthErrorProvider: React.FC<AuthErrorProviderProps> = ({ children }) => {
  
  return (
    <AuthErrorBoundary
      fallback={
        <AuthErrorContext.Provider value={{}}>
          {children}
          {/* No session dialog if error boundary triggers */}
        </AuthErrorContext.Provider>
      }
    >
      <AuthErrorProviderInner>{children}</AuthErrorProviderInner>
    </AuthErrorBoundary>
  );
};

export const useAuthError = (): AuthErrorContextType => {
  const context = useContext(AuthErrorContext);
  if (!context) {
    throw new Error('useAuthError must be used within an AuthErrorProvider');
  }
  return context;
};

export default AuthErrorProvider;
